import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IonIcon } from "@ionic/react";
import { closeOutline, lockClosedOutline, checkmarkCircle, openOutline } from "ionicons/icons";
import { Browser } from "@capacitor/browser";
import MintSlipLogo from "@/assests/mintslip-logo.png";
import { Stars } from "./Paywall";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import "@/styles/paywall.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SITE_URL = (process.env.REACT_APP_PUBLIC_SITE_URL || "https://mintslip.com").replace(/\/$/, "");
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// The native app sells nothing itself (the Spotify model): this sheet, in
// the web paywalls' exact clothes, pitches the account balance and links
// out to the website, where any amount is loaded with Stripe. Documents
// then spend their exact price from the balance; coming back to the app
// refreshes entitlements and the Download button is covered.
//
// props: onClose, docLabel?, previewImage?, mode (kept for call sites)
export default function NativePaywall({ onClose, docLabel, previewImage = null, mode = "all" }) {
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (!isUserLoggedIn()) return undefined;
    let alive = true;
    (async () => {
      try {
        const d = await (await fetch(`${BACKEND_URL}/api/wallet`, { headers: userHeaders() })).json();
        if (alive && d.success) setBalance(d.balance || 0);
      } catch { /* the pitch shows without it */ }
    })();
    return () => { alive = false; };
  }, []);

  const openSite = async () => {
    const url = `${SITE_URL}/app/billing?segment=wallet&from=app`; // straight onto the Wallet Balance segment
    try { await Browser.open({ url }); }
    catch { try { window.open(url, "_blank"); } catch { /* nothing to do */ } }
  };

  return createPortal(
    <div className="pw">
      {previewImage
        ? <img className="pw-bgimg" src={previewImage} alt="" aria-hidden="true" />
        : <span className="pw-bgfill" aria-hidden="true" />}
      <span className="pw-scrim" aria-hidden="true" />

      <button className="pw-x" onClick={() => onClose?.()} aria-label="Close">
        <IonIcon icon={closeOutline} />
      </button>

      <div className="pw-brandbar">
        <img className="pw-logo" src={MintSlipLogo} alt="MintSlip" />
      </div>

      <div className="pw-head pw-swap">
        <h1 className="pw-title">{docLabel ? `Download your ${docLabel}` : "Downloads & plans"}</h1>
        <p className="pw-subtitle">The #1 Document Creation App</p>
        <Stars />
      </div>

      <div className="pw-foot pw-swap">
        <div className="pw-feature"><IonIcon icon={checkmarkCircle} /> Load any amount for documents</div>
        <div className="pw-plans">
          <div className="pw-plan">
            <span className="pw-plan-info">
              <strong>Account balance</strong>
              <span>Loads once, never expires, works on every document</span>
            </span>
            {balance != null && <span className="pw-plan-price"><strong>{money(balance)}</strong></span>}
          </div>
        </div>
        <button className="pw-cta" onClick={openSite}>
          <IonIcon icon={lockClosedOutline} /> Continue on mintslip.com
        </button>
        <p className="pw-fine">
          <IonIcon icon={openOutline} style={{ fontSize: 12, verticalAlign: "-1px", marginRight: 4 }} />
          Payments are handled securely on our website. Sign in with this account and your balance appears here.
        </p>
      </div>
    </div>,
    document.body,
  );
}
